import React, { useState } from 'react';
import { Button, Popover, Form, Switch, Divider, Dropdown, Space, Typography, Radio } from 'antd';
import { Settings } from 'lucide-react';
import { useAtom } from 'jotai';
import useRecorderContext from '../context/useRecorderContext';
import { DeviceCapabilityHint } from './hints';
import { useDeviceCapabilityQuality, getQualityDropdownItems } from '../hooks/useDeviceCapabilityQuality';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { UpgradePrompt } from './overlays/UpgradePrompt';
import type { RecordingQuality } from '../context/RecorderContext';
import { isRatioTransitioningAtom, canvasTransitionSnapshotAtom } from '../store/canvasStore';
import { store } from '../store/canvasStoreInstance';
import { activeProjectAtom } from '../atoms/sceneAtoms';
import { projectsManager } from '../services/projectsManager';
import {
  landscapePresetsWithPreview,
  portraitPresetsWithPreview,
  squarePresetsWithPreview,
} from '../presets/layoutPresetsPreview';

const RatioIcon: React.FC<{ ratio: '16:9' | '1:1' | '9:16'; selected?: boolean }> = ({ ratio, selected }) => {
  const strokeColor = selected ? '#4A3CFA' : '#9CA3AF';
  const fillColor = selected ? 'rgba(74, 60, 250, 0.06)' : 'none';
  
  const shapes = {
    '16:9': { w: 28, h: 16, vw: 30, vh: 18 },
    '1:1': { w: 18, h: 18, vw: 20, vh: 20 },
    '9:16': { w: 12, h: 20, vw: 14, vh: 22 },
  };
  
  const s = shapes[ratio];
  
  return (
    <svg width={s.vw} height={s.vh} viewBox={`0 0 ${s.vw} ${s.vh}`} xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width={s.w} height={s.h} rx="2" stroke={strokeColor} strokeWidth="1.5" fill={fillColor} />
    </svg>
  );
};

const { Title } = Typography;

interface ProjectSettingsButtonProps {}

export const ProjectSettingsButton: React.FC<ProjectSettingsButtonProps> = () => {
  const {
    enableCountdown,
    setEnableCountdown,
    showGrid,
    setShowGrid,
    recordingQuality,
    setRecordingQuality,
    stageRef,
    canvasRatio,
    setCanvasRatio,
    setSelectedPreset,
    setInitialStreamLayout,
  } = useRecorderContext();

  const { deviceTier, qualityConfig } = useDeviceCapabilityQuality();
  const { isPaid } = useFeatureAccess();
  const [settingsForm] = Form.useForm();
  
  // Get active project to check if it's a blank template project (no templateId)
  const [activeProject, setActiveProject] = useAtom(activeProjectAtom);
  const isBlankTemplateProject = activeProject && activeProject.templateId == null;
  
  // Local upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptFeature, setUpgradePromptFeature] = useState("");
  const [upgradePromptDescription, setUpgradePromptDescription] = useState("");

  return (
    <>
    <Popover
      trigger="click"
      placement="bottomRight"
      title={
        <Title level={5} style={{ margin: '8px 0 4px', paddingInline: 8 }}>
          Project Settings
        </Title>
      }
      content={
        <div style={{ width: 280, padding: '8px 8px 12px' }}>
          <Form
            form={settingsForm}
            layout="vertical"
            initialValues={{
              countdown: enableCountdown,
            }}
            style={{ width: '100%' }}
          >
            <Form.Item
              label="Show Countdown"
              name="countdown"
              layout="horizontal"
              labelAlign="left"
              colon={false}
              labelCol={{ span: 16 }}
            >
              <Switch
                style={{
                  marginInlineStart: 'auto',
                  display: 'flex',
                }}
                checked={enableCountdown}
                onChange={(checked) => setEnableCountdown(checked)}
              />
            </Form.Item>
            <Form.Item
              label="Show Guides"
              name="guides"
              layout="horizontal"
              labelAlign="left"
              colon={false}
              labelCol={{ span: 16 }}
            >
              <Switch
                style={{
                  marginInlineStart: 'auto',
                  display: 'flex',
                }}
                checked={showGrid}
                onChange={(checked) => setShowGrid(checked)}
              />
            </Form.Item>
            <Divider
              style={{
                marginBlock: '16px 12px',
                borderBlockStart: '1px solid rgba(0,0,0,0.08)',
              }}
            />
            {isBlankTemplateProject && (
              <Form.Item
                label="Aspect Ratio"
                layout="vertical"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ marginBottom: 16 }}
              >
                <Radio.Group
                  value={canvasRatio}
                  onChange={(e) => {
                    const value = e.target.value;
                    store.set(isRatioTransitioningAtom, true);
                    
                    if (activeProject) {
                      setActiveProject({
                        ...activeProject,
                        aspectRatio: value,
                      });
                      
                      const projectIdNum = parseInt(activeProject.id, 10);
                      if (!isNaN(projectIdNum)) {
                        projectsManager.updateProject(projectIdNum, { aspectRatio: value }).catch(() => {});
                      }
                    }
                    
                    setCanvasRatio(value);
                    window.dispatchEvent(new CustomEvent("manual-preset-change"));
                    
                    let presetPool: any[] = [];
                    if (value === "16:9") {
                      presetPool = landscapePresetsWithPreview;
                    } else if (value === "9:16") {
                      presetPool = portraitPresetsWithPreview;
                    } else if (value === "1:1") {
                      presetPool = squarePresetsWithPreview;
                    }
                    
                    const firstPreset = presetPool[0];
                    if (firstPreset) {
                      setSelectedPreset(firstPreset.key);
                      setInitialStreamLayout([]);
                    }
                  }}
                  className="aspect-ratio-selector"
                >
                  {(['16:9', '1:1', '9:16'] as const).map((ratio) => (
                    <Radio.Button
                      key={ratio}
                      value={ratio}
                      className={`aspect-ratio-option ${canvasRatio === ratio ? 'selected' : ''}`}
                    >
                      <RatioIcon ratio={ratio} selected={canvasRatio === ratio} />
                      <span className="ratio-label">{ratio}</span>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            )}
            {qualityConfig?.showHint && deviceTier && (
              <DeviceCapabilityHint deviceTier={deviceTier} visible={true} />
            )}
            <Form.Item
              label="Recording Quality"
              name="quality"
              layout="vertical"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Dropdown
                menu={{
                  items: getQualityDropdownItems(
                    qualityConfig,
                    !isPaid,
                    recordingQuality
                  ),
                  selectable: true,
                  selectedKeys: [recordingQuality],
                  onClick: ({ key }) => {
                    if (
                      (key === '1080p' || key === '2k') &&
                      !isPaid
                    ) {
                      const qualityName =
                        key === '1080p' ? '1080p Recording' : '2k Recording';
                      const description =
                        key === '1080p'
                          ? 'Upgrade to Pro to unlock high-definition 1080p recording for sharper, more professional videos.'
                          : 'Upgrade to Pro to unlock ultra-high-definition 2k recording with enhanced detail for larger monitors.';

                      setUpgradePromptFeature(qualityName);
                      setUpgradePromptDescription(description);
                      setShowUpgradePrompt(true);
                      return;
                    }

                    // Capture snapshot of current canvas with dimensions and offsets before resize
                    try {
                      const canvas = stageRef?.current?.getCanvas?.();
                      if (canvas) {
                        const lowerCanvas = canvas.lowerCanvasEl;
                        const canvasRect = lowerCanvas?.getBoundingClientRect();
                        const containerRect = lowerCanvas?.parentElement?.getBoundingClientRect();
                        const snapshot = canvas.toDataURL({ format: 'png', quality: 1 });
                        store.set(canvasTransitionSnapshotAtom, {
                          dataUrl: snapshot,
                          width: canvasRect?.width || canvas.width,
                          height: canvasRect?.height || canvas.height,
                          offsetLeft: containerRect && canvasRect ? canvasRect.left - containerRect.left : 0,
                          offsetTop: containerRect && canvasRect ? canvasRect.top - containerRect.top : 0,
                        });
                      }
                    } catch (e) {
                      // Fallback: continue without snapshot
                    }
                    // Set transitioning flag before quality change to hide objects during resize
                    store.set(isRatioTransitioningAtom, true);
                    setRecordingQuality(key as RecordingQuality);
                  },
                }}
                trigger={['click']}
              >
                <Button type="default" style={{ width: '100%', textAlign: 'left' }}>
                  <Space>
                    {recordingQuality === '720p' && '720p (HD)'}
                    {recordingQuality === '1080p' && '1080p (Full HD)'}
                    {recordingQuality === '2k' && '2k (Quad HD)'}
                  </Space>
                </Button>
              </Dropdown>
            </Form.Item>
          </Form>
        </div>
      }
    >
      <Button
        type="text"
        size="large"
        icon={<Settings size={20} />}
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          borderRadius: 8,
        }}
        aria-label="Project settings"
      />
    </Popover>
    
    <UpgradePrompt
      visible={showUpgradePrompt}
      onClose={() => setShowUpgradePrompt(false)}
      feature={upgradePromptFeature}
      description={upgradePromptDescription}
    />
  </>
  );
};

export default ProjectSettingsButton;
